/**
 * Utilitaires pour la gestion des statuts de lecture et d'état des livres
 */

import React, { useState } from 'react';
import { Box, Chip, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Retourne la liste des statuts de lecture avec leurs traductions
 * @param {Function} t - Fonction de traduction de react-i18next
 * @param {boolean} includeAll - Si true, inclut l'option "Tous les statuts"
 * @returns {Array} Liste des statuts avec leurs labels traduits
 */
export const getStatutsLectureTranslated = (t, includeAll = false) => {
  const statuts = [
    { value: 'indefini', label: t('booksPage.readingStatus.indefini') },
    { value: 'non_lu', label: t('booksPage.readingStatus.non_lu') },
    { value: 'en_cours', label: t('booksPage.readingStatus.en_cours') },
    { value: 'lu', label: t('booksPage.readingStatus.lu') },
    { value: 'abandonne', label: t('booksPage.readingStatus.abandonne') }
  ];
  
  if (includeAll) {
    return [
      { value: '', label: t('booksPage.readingStatus.all') },
      ...statuts
    ];
  }
  
  return statuts;
};

/**
 * Retourne la couleur du chip pour un statut de lecture donné
 * @param {string|null|undefined} statut - Le statut de lecture
 * @returns {string} La couleur du chip Material-UI
 */
export const getStatutColor = (statut) => {
  switch (statut) {
    case 'lu': return 'success';
    case 'en_cours': return 'warning';
    case 'non_lu': return 'default';
    case 'abandonne': return 'error';
    case 'indefini': return 'info';
    case null:
    case undefined:
    case '':
      return 'info';
    default: return 'default';
  }
};

/**
 * Retourne le libellé affiché pour un statut de lecture donné
 * @param {string|null|undefined} statut - Le statut de lecture
 * @returns {string} Le libellé à afficher
 */
export const getStatutLabel = (statut) => {
  switch (statut) {
    case 'lu': return 'Lu';
    case 'en_cours': return 'En cours de lecture';
    case 'non_lu': return 'Non lu';
    case 'abandonne': return 'Abandonné';
    case 'indefini': return 'Indéfini';
    case null:
    case undefined:
    case '':
      return 'Indéfini';
    default: return statut;
  }
};

/**
 * Retourne le libellé court affiché pour un statut de lecture donné (pour les cartes)
 * @param {string|null|undefined} statut - Le statut de lecture
 * @returns {string} Le libellé court à afficher
 */
export const getStatutLabelShort = (statut) => {
  switch (statut) {
    case 'lu': return 'Lu';
    case 'en_cours': return 'En cours';
    case 'non_lu': return 'Non lu';
    case 'abandonne': return 'Abandonné';
    case 'indefini': return 'Indéfini';
    case null:
    case undefined:
    case '':
      return 'Indéfini';
    default: return statut;
  }
};

/**
 * Retourne la liste des états de livre avec leurs traductions
 * @param {Function} t - Fonction de traduction de react-i18next
 * @returns {Array} Liste des états avec leurs labels traduits
 */
export const getEtatsLivreTranslated = (t) => {
  return [
    { value: 'indefini', label: t('bookCondition.indefini') },
    { value: 'excellent', label: t('bookCondition.excellent') },
    { value: 'bon', label: t('bookCondition.bon') },
    { value: 'moyen', label: t('bookCondition.moyen') },
    { value: 'mauvais', label: t('bookCondition.mauvais') }
  ];
};

/**
 * Retourne le libellé affiché pour un état de livre donné
 * @param {string|null|undefined} etat - L'état du livre
 * @param {Function} t - Fonction de traduction de react-i18next (optionnelle)
 * @returns {string} Le libellé à afficher
 */
export const getEtatLabel = (etat, t = null) => {
  if (t) {
    // Utiliser les traductions si disponible
    switch (etat) {
      case 'excellent': return t('bookCondition.excellent');
      case 'bon': return t('bookCondition.bon');
      case 'moyen': return t('bookCondition.moyen');
      case 'mauvais': return t('bookCondition.mauvais');
      case 'indefini': return t('bookCondition.indefini');
      case null:
      case undefined:
      case '':
        return t('bookCondition.indefini');
      default: return etat;
    }
  }
  
  // Fallback sans traduction (pour compatibilité)
  switch (etat) {
    case 'excellent': return 'Excellent';
    case 'bon': return 'Bon';
    case 'moyen': return 'Moyen';
    case 'mauvais': return 'Mauvais';
    case 'indefini': return 'Indéfini';
    case null:
    case undefined:
    case '':
      return 'Indéfini';
    default: return etat;
  }
};

/**
 * Liste des statuts de lecture disponibles pour les filtres
 */
export const statutsLecture = [
  { value: '', label: 'Tous les statuts' },
  { value: 'indefini', label: 'Indéfini' },
  { value: 'non_lu', label: 'Non lu' },
  { value: 'en_cours', label: 'En cours de lecture' },
  { value: 'lu', label: 'Lu' },
  { value: 'abandonne', label: 'Abandonné' },
];

/**
 * Composant pour afficher et modifier le statut de lecture d'un livre
 */
export function StatusChip({ 
  statut, 
  onStatusChange, 
  isLoading = false,
  editable = true 
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const statusOptions = [
    { value: 'indefini', label: t('booksPage.readingStatus.indefini'), color: '#9e9e9e' },
    { value: 'non_lu', label: t('booksPage.readingStatus.non_lu'), color: '#ff9800' },
    { value: 'en_cours', label: t('booksPage.readingStatus.en_cours_short'), color: '#2196f3' },
    { value: 'lu', label: t('booksPage.readingStatus.lu'), color: '#4caf50' },
    { value: 'abandonne', label: t('booksPage.readingStatus.abandonne'), color: '#f44336' }
  ];

  const currentStatus = statusOptions.find(option => option.value === statut) || statusOptions[0];

  const handleClick = (event) => {
    if (editable && !isLoading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (newStatus) => {
    if (onStatusChange && newStatus !== statut) {
      onStatusChange(newStatus);
    }
    handleClose();
  };

  return (
    <>
      <Chip
        label={currentStatus.label}
        onClick={handleClick}
        disabled={!editable || isLoading}
        sx={{
          backgroundColor: currentStatus.color,
          color: 'white',
          fontWeight: 'bold',
          cursor: editable ? 'pointer' : 'default',
          '&:hover': editable ? {
            backgroundColor: currentStatus.color,
            opacity: 0.8
          } : {},
          '&:disabled': {
            backgroundColor: currentStatus.color,
            opacity: 0.6
          }
        }}
        size="small"
      />
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusSelect(option.value)}
            selected={option.value === statut}
            sx={{
              backgroundColor: option.value === statut ? option.color + '20' : 'transparent',
              '&:hover': {
                backgroundColor: option.color + '10'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: option.color,
                  mr: 1
                }}
              />
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
